import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { ILocalAsset, IAssetInDTO } from '../../../../models/assets/assets.model';
import { assetConverter } from '../../../../models/assets/assets-converter';
import { AssetType } from '../../../../models/defines';
import {
    AssetBrowserQuery,
    AssetLocation,
    DEFAULT_ASSET_LOCATION,
} from '../../../../pages/private/dashboard/assets/models/asset-browser.model';
import { WorkspaceAssetEventListenerService } from '../../../state-management/workspace/socket/listeners/workspace-asset-event-listener.service';
import {
    IAssetBrowserPagination,
    IAssetFolderDeleteCommand,
    IGetAssetsParams,
    IPaginationMeta,
} from '../../api/associations/workspace-asset-request.types';
import { WorkspaceAssetApiManagerService } from '../../api/managers/associations/workspace-asset-api-manager.service';
import { WorkspaceStateManagerService } from '../workspace-state-manager.service';

interface ILocationPaginationCursors {
    folderPage: number;
    assetPage: number;
    limit: number;
}

export interface IAssetUsageError {
    code: string;
    message: string;
    usages: Array<{ kind: string; sceneId?: string; takeId?: string; positionId?: string }>;
}

@Injectable()
export class WorkspaceAssetStateManagerService {
    public readonly onGeneratedVideo$ = this.workspaceAssetEventListener.onGeneratedVideo$;

    private readonly foldersSubject = new BehaviorSubject<ILocalAsset[] | null>(null);
    public readonly folders$ = this.foldersSubject.asObservable();

    private readonly foldersPaginationSubject = new BehaviorSubject<IPaginationMeta | null>(null);
    public readonly foldersPagination$ = this.foldersPaginationSubject.asObservable();

    private readonly itemsSubject = new BehaviorSubject<ILocalAsset[] | null>(null);
    public readonly items$ = this.itemsSubject.asObservable();
    // Compatibility aliases for legacy asset surfaces.
    public readonly assets$ = this.items$;

    private readonly assetsPaginationSubject = new BehaviorSubject<IPaginationMeta | null>(null);
    public readonly assetsPagination$ = this.assetsPaginationSubject.asObservable();

    private readonly paginationSubject = new BehaviorSubject<IAssetBrowserPagination>({ folders: null, assets: null });
    public readonly pagination$ = this.paginationSubject.asObservable();

    private readonly loadingSubject = new BehaviorSubject<boolean>(false);
    public readonly loading$ = this.loadingSubject.asObservable();
    public readonly isLoading$ = this.loading$;

    private readonly foldersLoadingSubject = new BehaviorSubject<boolean>(false);
    public readonly isFoldersLoading$ = this.foldersLoadingSubject.asObservable();

    private readonly itemsLoadingSubject = new BehaviorSubject<boolean>(false);
    public readonly isAssetsLoading$ = this.itemsLoadingSubject.asObservable();

    private readonly errorSubject = new BehaviorSubject<string | null>(null);
    public readonly error$ = this.errorSubject.asObservable();

    private readonly usageErrorSubject = new BehaviorSubject<IAssetUsageError | null>(null);
    public readonly usageError$ = this.usageErrorSubject.asObservable();

    private readonly currentFolderNameSubject = new BehaviorSubject<string | null>('/');
    public readonly currentFolderName$ = this.currentFolderNameSubject.asObservable();

    private readonly locationSubject = new BehaviorSubject<AssetLocation>(this.cloneLocation(DEFAULT_ASSET_LOCATION));
    public readonly location$: Observable<AssetLocation> = this.locationSubject.asObservable();

    private readonly paginationByLocation = new Map<string, ILocationPaginationCursors>();
    private readonly workspaceResetSubject = new Subject<void>();
    public readonly workspaceReset$ = this.workspaceResetSubject.asObservable();
    private currentWorkspaceId: string | null = null;
    private latestRequestId = 0;

    constructor(
        private wsStateService: WorkspaceStateManagerService,
        private workspaceAssetEventListener: WorkspaceAssetEventListenerService,
        private workspaceAssetApiManager: WorkspaceAssetApiManagerService
    ) {
        this.wsStateService.currentWorkspaceId$.subscribe((id) => {
            if (this.currentWorkspaceId === id) {
                return;
            }
            this.reset();
            this.currentWorkspaceId = id;
            this.workspaceResetSubject.next();
            if (id) void this.loadAssetsAsync();
        });

        this.workspaceAssetEventListener.onAssetDeleted$.subscribe((data) => data?.assetId && void this.refreshActiveLocationAsync());
        this.workspaceAssetEventListener.onAssetCreated$.subscribe((data) => data?.assetId && void this.refreshActiveLocationAsync());
        this.workspaceAssetEventListener.onAssetUpdated$.subscribe((data) => data?.assetId && void this.refreshActiveLocationAsync());
    }

    public get itemsSnapshot(): ILocalAsset[] {
        return [...(this.itemsSubject.value ?? [])];
    }

    public get foldersSnapshot(): ILocalAsset[] {
        return [...(this.foldersSubject.value ?? [])];
    }

    public get errorSnapshot(): string | null {
        return this.errorSubject.value;
    }

    public get locationSnapshot(): AssetLocation {
        return this.locationSubject.value;
    }

    public async loadLocationAsync(location: AssetLocation, updateOnly?: 'folders' | 'assets', append = false): Promise<void> {
        if (!this.currentWorkspaceId) return;
        const nextLocation = this.cloneLocation(location);
        const requestId = ++this.latestRequestId;
        if (!append) {
            const previous = this.getPagination(nextLocation);
            this.paginationByLocation.set(this.locationKey(nextLocation), { folderPage: 1, assetPage: 1, limit: previous.limit });
        }
        const pagination = this.getPagination(nextLocation);

        this.locationSubject.next(nextLocation);
        this.currentFolderNameSubject.next(this.getFolderName(nextLocation));
        this.loadingSubject.next(true);
        this.errorSubject.next(null);
        this.usageErrorSubject.next(null);
        if (!updateOnly || updateOnly === 'folders') {
            this.foldersLoadingSubject.next(true);
        }
        if (!updateOnly || updateOnly === 'assets') {
            this.itemsLoadingSubject.next(true);
        }

        const { data, error } = await this.workspaceAssetApiManager.getAssetsInWorkspaceAsync(
            this.toAssetBrowserQuery(nextLocation, pagination)
        );

        if (requestId !== this.latestRequestId) {
            return;
        }

        this.loadingSubject.next(false);
        this.foldersLoadingSubject.next(false);
        this.itemsLoadingSubject.next(false);

        if (error || !data) {
            this.reportError(error, 'An error occurred when trying to get assets for workspace');
            return;
        }

        this.applyResult(data, updateOnly, append);
    }

    public async trashAsync(assetIds: string[]): Promise<boolean> {
        const currentItems = this.itemsSnapshot;
        const currentFolders = this.foldersSnapshot;
        const mutationLocation = this.locationKey(this.locationSnapshot);
        const mutationRequestId = this.latestRequestId;
        this.removeVisibleAssets(assetIds);

        const { error } = await this.workspaceAssetApiManager.trashAssetsAsync(assetIds);
        if (!error) {
            return true;
        }

        if (this.isCurrentMutation(mutationLocation, mutationRequestId)) {
            this.itemsSubject.next(currentItems);
            this.foldersSubject.next(currentFolders);
        }
        this.reportError(error, 'Could not move assets to Trash');
        return false;
    }

    public async restoreAsync(assetIds: string[]): Promise<boolean> {
        const currentItems = this.itemsSnapshot;
        const currentFolders = this.foldersSnapshot;
        const mutationLocation = this.locationKey(this.locationSnapshot);
        const mutationRequestId = this.latestRequestId;
        if (this.locationSnapshot.scope === 'trash') {
            this.removeVisibleAssets(assetIds);
        }

        const { error } = await this.workspaceAssetApiManager.restoreAssetsAsync(assetIds);
        if (!error) {
            return true;
        }

        if (this.isCurrentMutation(mutationLocation, mutationRequestId)) {
            this.itemsSubject.next(currentItems);
            this.foldersSubject.next(currentFolders);
        }
        this.reportError(error, 'Could not restore assets');
        return false;
    }

    public async permanentlyRemoveAsync(assetIds: string[]): Promise<boolean> {
        const currentItems = this.itemsSnapshot;
        const currentFolders = this.foldersSnapshot;
        const mutationLocation = this.locationKey(this.locationSnapshot);
        const mutationRequestId = this.latestRequestId;
        this.removeVisibleAssets(assetIds);

        const { error } = await this.workspaceAssetApiManager.permanentlyRemoveAssetsAsync(assetIds);
        if (!error) {
            return true;
        }
        if (this.isCurrentMutation(mutationLocation, mutationRequestId)) {
            this.itemsSubject.next(currentItems);
            this.foldersSubject.next(currentFolders);
        }
        this.reportError(error, 'Could not permanently delete assets');
        return false;
    }

    public async removeFromProjectAsync(assetIds: string[]): Promise<boolean> {
        const projectId = this.locationSnapshot.projectId;
        if (!projectId) {
            this.errorSubject.next('An active project is required to remove assets from a project');
            return false;
        }

        const currentItems = this.itemsSnapshot;
        const currentFolders = this.foldersSnapshot;
        const mutationLocation = this.locationKey(this.locationSnapshot);
        const mutationRequestId = this.latestRequestId;
        this.removeVisibleAssets(assetIds);

        const { error } = await this.workspaceAssetApiManager.removeFromProjectAsync(projectId, assetIds);
        if (!error) {
            return true;
        }

        const partial = (error as { error?: { removedAssetIds?: string[] } } | null)?.error?.removedAssetIds ?? [];
        if (partial.length) {
            if (this.isCurrentMutation(mutationLocation, mutationRequestId)) {
                this.itemsSubject.next(currentItems.filter((item) => !partial.includes(item._id)));
                this.foldersSubject.next(currentFolders.filter((folder) => !partial.includes(folder._id)));
                await this.loadAssetsAsync();
                if (this.locationKey(this.locationSnapshot) === mutationLocation) {
                    this.reportError(error, 'Some assets are still used in this project');
                }
            }
            return false;
        }

        if (this.isCurrentMutation(mutationLocation, mutationRequestId)) {
            this.itemsSubject.next(currentItems);
            this.foldersSubject.next(currentFolders);
        }
        this.reportError(error, 'Could not remove assets from project');
        return false;
    }

    public async renameFolderAsync(assetId: string, newName: string): Promise<boolean> {
        const { data, error } = await this.workspaceAssetApiManager.renameFolderAsync(assetId, newName);
        if (error || !data) {
            this.reportError(error, 'Could not rename folder');
            return false;
        }

        const folder = assetConverter.convertInAssetToLocal(data);
        this.foldersSubject.next(this.foldersSnapshot.map((item) => (item._id === assetId ? folder : item)));
        return true;
    }

    public async deleteFolderAsync(assetId: string, children: IAssetFolderDeleteCommand['children']): Promise<boolean> {
        const currentFolders = this.foldersSnapshot;
        const mutationLocation = this.locationKey(this.locationSnapshot);
        const mutationRequestId = this.latestRequestId;
        this.foldersSubject.next(currentFolders.filter((folder) => folder._id !== assetId));

        const { error } = await this.workspaceAssetApiManager.deleteFolderAsync(assetId, { children });
        if (!error) {
            return true;
        }

        if (this.isCurrentMutation(mutationLocation, mutationRequestId)) {
            this.foldersSubject.next(currentFolders);
        }
        this.reportError(error, 'Could not delete folder');
        return false;
    }

    public enterFolder(folderName: string): void {
        void this.loadLocationAsync({ ...this.locationSnapshot, folderPath: [folderName] });
    }

    public exitFolder(): void {
        void this.loadLocationAsync({ ...this.locationSnapshot, folderPath: [] });
    }

    public setFolderPage(page: number): void {
        const pagination = this.getPagination(this.locationSnapshot);
        pagination.folderPage = page;
        this.paginationByLocation.set(this.locationKey(this.locationSnapshot), pagination);
        void this.loadLocationAsync(this.locationSnapshot, 'folders', page > 1);
    }

    public setAssetPage(page: number): void {
        const pagination = this.getPagination(this.locationSnapshot);
        pagination.assetPage = page;
        this.paginationByLocation.set(this.locationKey(this.locationSnapshot), pagination);
        void this.loadLocationAsync(this.locationSnapshot, 'assets', page > 1);
    }

    public loadMoreAssets(): void {
        const pagination = this.assetsPaginationSubject.value;
        if (pagination?.hasMore) {
            this.setAssetPage(pagination.page + 1);
        }
    }

    public loadMoreFolders(): void {
        const pagination = this.foldersPaginationSubject.value;
        if (pagination?.hasMore) {
            this.setFolderPage(pagination.page + 1);
        }
    }

    public setFilterType(filterType: AssetType | null): void {
        void this.loadLocationAsync({ ...this.locationSnapshot, filters: { type: filterType } });
    }

    public getCurrentFolderName(): string {
        return this.currentFolderNameSubject.value;
    }

    public loadAssetsAsync(updateOnly?: 'folders' | 'assets'): Promise<void> {
        return this.loadLocationAsync(this.locationSnapshot, updateOnly);
    }

    public getCurrentFolders(): ILocalAsset[] {
        return this.foldersSnapshot;
    }

    private applyResult(data: { folders: { items: IAssetInDTO[] }; assets: { items: IAssetInDTO[] }; pagination: IAssetBrowserPagination }, updateOnly: 'folders' | 'assets' | undefined, append: boolean): void {
        const pagination = { ...this.paginationSubject.value };

        if (!updateOnly || updateOnly === 'folders') {
            const folders = assetConverter.convertArrayOfInAssetsToLocal(data.folders.items);
            this.foldersSubject.next(append ? this.mergeAssets(this.foldersSnapshot, folders) : folders);
            this.foldersPaginationSubject.next(data.pagination.folders);
            pagination.folders = data.pagination.folders;
        }
        if (!updateOnly || updateOnly === 'assets') {
            const items = assetConverter.convertArrayOfInAssetsToLocal(data.assets.items);
            this.itemsSubject.next(append ? this.mergeAssets(this.itemsSnapshot, items) : items);
            this.assetsPaginationSubject.next(data.pagination.assets);
            pagination.assets = data.pagination.assets;
        }

        this.paginationSubject.next({ ...pagination });
    }

    private async refreshActiveLocationAsync(): Promise<void> {
        const location = this.locationSnapshot;
        await this.loadLocationAsync(location);
    }

    private removeVisibleAssets(assetIds: string[]): void {
        const assetIdSet = new Set(assetIds);
        this.itemsSubject.next(this.itemsSnapshot.filter((item) => !assetIdSet.has(item._id)));
        this.foldersSubject.next(this.foldersSnapshot.filter((item) => !assetIdSet.has(item._id)));
    }

    private mergeAssets(current: ILocalAsset[], incoming: ILocalAsset[]): ILocalAsset[] {
        const byId = new Map(current.map((asset) => [asset._id, asset]));
        incoming.forEach((asset) => byId.set(asset._id, asset));
        return [...byId.values()];
    }

    private isCurrentMutation(locationKey: string, requestId: number): boolean {
        return requestId === this.latestRequestId && locationKey === this.locationKey(this.locationSnapshot);
    }

    private reportError(error: unknown, fallback: string): void {
        const response = (error as { error?: Partial<IAssetUsageError>; message?: string } | null)?.error;
        const blocked = (response as { blocked?: Array<IAssetUsageError['usages'][number] | { usages?: IAssetUsageError['usages'] }> } | undefined)?.blocked;
        if (response?.code === 'ASSET_IN_USE' || Array.isArray(blocked)) {
            const usages = response?.usages ?? (blocked ?? []).flatMap((entry) => 'usages' in entry && Array.isArray(entry.usages) ? entry.usages : [entry as IAssetUsageError['usages'][number]]);
            const usageError: IAssetUsageError = { code: response?.code ?? 'ASSET_IN_USE', message: response?.message ?? fallback, usages };
            this.usageErrorSubject.next(usageError);
            this.errorSubject.next(usageError.message);
            return;
        }
        this.errorSubject.next((error as { message?: string } | null)?.message ?? fallback);
    }

    private matchesCurrentLocation(asset: IAssetInDTO, location: AssetLocation): boolean {
        if (location.scope !== 'workspace' || asset.deletedAt || asset.folder !== this.getFolderName(location)) {
            return false;
        }
        if (location.filters.type && location.filters.type !== asset.type) {
            return false;
        }
        if (location.search) {
            return (asset.displayName ?? asset.folder).toLowerCase().includes(location.search.toLowerCase());
        }
        return true;
    }

    private toAssetBrowserQuery(location: AssetLocation, pagination: ILocationPaginationCursors): IGetAssetsParams {
        const query: AssetBrowserQuery = {
            scope: location.scope,
            projectId: location.projectId,
            collection: location.collection,
            folderPath: [...location.folderPath],
            search: location.search,
            filters: { ...location.filters },
            sort: { ...location.sort },
        };

        return {
            scope: query.scope,
            projectId: query.projectId,
            collection: query.collection,
            folderName: this.getFolderName(location),
            search: query.search,
            filterByType: query.filters.type,
            sortBy: query.sort.by,
            sortDirection: query.sort.direction,
            limit: pagination.limit,
            folderPage: pagination.folderPage,
            assetPage: pagination.assetPage,
        };
    }

    private getPagination(location: AssetLocation): ILocationPaginationCursors {
        const key = this.locationKey(location);
        const pagination = this.paginationByLocation.get(key) ?? { folderPage: 1, assetPage: 1, limit: 10 };
        return { ...pagination };
    }

    private getFolderName(location: AssetLocation): string {
        return location.folderPath[location.folderPath.length - 1] ?? '/';
    }

    private locationKey(location: AssetLocation): string {
        return JSON.stringify({
            scope: location.scope,
            projectId: location.projectId,
            collection: location.collection,
            folderPath: location.folderPath,
            search: location.search,
            filters: location.filters,
            sort: location.sort,
        });
    }

    private cloneLocation(location: AssetLocation): AssetLocation {
        return {
            ...location,
            folderPath: [...location.folderPath],
            filters: { ...location.filters },
            sort: { ...location.sort },
        };
    }

    private reset(): void {
        this.currentWorkspaceId = null;
        this.latestRequestId += 1;
        this.paginationByLocation.clear();
        this.locationSubject.next(this.cloneLocation(DEFAULT_ASSET_LOCATION));
        this.currentFolderNameSubject.next('/');
        this.foldersSubject.next(null);
        this.itemsSubject.next(null);
        this.foldersPaginationSubject.next(null);
        this.assetsPaginationSubject.next(null);
        this.paginationSubject.next({ folders: null, assets: null });
        this.errorSubject.next(null);
        this.usageErrorSubject.next(null);
        this.loadingSubject.next(false);
        this.foldersLoadingSubject.next(false);
        this.itemsLoadingSubject.next(false);
    }
}
